import React from "react";
import ConnectedField from "components/ConnectedField";
import { useFormikContext } from "formik";
import {
  COUPON_DISCOUNT_TYPE,
  COUPON_DURATION,
  COUPON_DURATION_OPTIONS
} from "utils/constants/coupon";
import { FORM_KEY } from "utils/constants/form";
import SegmentedControl from "components/SegmentedControl";
import {
  getFieldPath,
  integerTransformer,
  percentTransformer
} from "utils/form";
import AmountField from "components/AmountField";
import startCase from "lodash/startCase";
import get from "lodash/get";
import CurrencyInput from "components/FormerEditor/CurrencyInput";
import SelectField from "components/SelectField";
import SectionBezel from "components/SectionBezel";
import { getIconForUID } from "components/FormerEditor/utils";

const DiscountFields = ({ name }) => {
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();

  const canEdit = !Boolean(values.id);
  const LockIcon = getIconForUID("lock");
  const locked = !canEdit && <LockIcon size={18} />;

  const amountOffPath = getFieldPath(name, "amount_off");
  const percentOffPath = getFieldPath(name, "percent_off");

  const currencyPath = getFieldPath(name, "currency");
  const durationPath = getFieldPath(name, "duration");
  const duration = get(values, durationPath);
  const discountTypePath = getFieldPath(name, `${FORM_KEY}.discount_type`);

  const idPath = getFieldPath(name, "id");
  const id = get(values, idPath);
  const editMode = Boolean(id);

  const discountType = get(values, discountTypePath);
  const amountOff = get(values, amountOffPath);
  const percentOff = get(values, percentOffPath);
  const currency = get(values, currencyPath);

  const percentTouched = get(touched, percentOffPath);

  const segmentProps = {
    options: Object.values(COUPON_DISCOUNT_TYPE).map((type) => ({
      uid: type,
      label: startCase(type)
    })),
    disabled: editMode,
    theme: "slim",
    value: discountType,
    onClick: (_, selection) => {
      setFieldTouched(discountTypePath, true);
      setFieldValue(discountTypePath, selection);
    }
  };

  const inputProps = {
    placeholder: `0%`,
    type: "text",
    disabled: editMode,
    className: "input--common br-6",
    value: percentOff,
    onChange: ({ currentTarget }) => {
      if (!percentTouched) {
        setFieldTouched(percentOffPath, true);
      }
      setFieldValue(percentOffPath, percentTransformer(currentTarget.value));
    }
  };
  const maskOptions = {
    prefix: "",
    suffix: "%",
    integerLimit: 3
  };

  return (
    <SectionBezel header="Discount" action={locked}>
      <div className="w-100 flex flex-column justify-between items-center">
        <div className="f5 w-100 fw5">{`Type`}</div>
        <div className="f5 w-100 pt3">
          <SegmentedControl {...segmentProps} />
        </div>
      </div>
      <div className="pt3">
        <div className="w-100 flex flex-column justify-between items-center">
          <div className="f5 w-100 fw5">{`Amount`}</div>
          <div className="f5 w-100 pt3">
            {discountType === COUPON_DISCOUNT_TYPE.PERCENTAGE ? (
              <CurrencyInput maskOptions={maskOptions} {...inputProps} />
            ) : (
              <AmountField
                disabled={editMode}
                value={amountOff}
                name={amountOffPath}
                currency={currency}
                currencyPath={currencyPath}
              />
            )}
          </div>
        </div>
      </div>
      <div className="pt3">
        <SelectField
          disabled={editMode}
          name={getFieldPath(name, "duration")}
          label="Duration"
          options={COUPON_DURATION_OPTIONS}
          customClasses={{ label: "field__label fw5 db" }}
        />
      </div>
      {duration === COUPON_DURATION.REPEATING && (
        <div className="pt3">
          <ConnectedField
            disabled={editMode}
            name={getFieldPath(name, "duration_in_months")}
            label="Duration in months"
            transformer={integerTransformer}
          />
        </div>
      )}
    </SectionBezel>
  );
};

export default DiscountFields;
